import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  PieChart, 
  TrendingUp, 
  TrendingDown, 
  Download, 
  Visibility, 
  Schedule, 
  Search, 
  FilterList, 
  MoreVert,
  AttachMoney,
  People,
  Business,
  Assessment,
  CalendarToday,
  Star,
  CheckCircle,
  Warning,
  Delete
} from '@mui/icons-material';

const ReportsDashboard = () => {
  const [reports, setReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    // Mock data for RM360 reports
    const mockReports = [
      {
        id: 1,
        title: 'Q4 2023 Portfolio Performance Report',
        type: 'Portfolio Analysis',
        category: 'Performance',
        status: 'Completed',
        priority: 'High',
        generatedDate: '2024-01-15',
        lastUpdated: '2 hours ago',
        customerCount: 45,
        totalValue: 2500000,
        format: 'PDF',
        size: '2.4 MB',
        generatedBy: 'Al Razzaq',
        description: 'Comprehensive analysis of portfolio performance for Q4 2023, including risk assessment and recommendations.',
        tags: ['Portfolio', 'Performance', 'Q4 2023', 'Risk Analysis'],
        scheduled: false
      },
      {
        id: 2,
        title: 'Customer Engagement Analysis - December 2023',
        type: 'Customer Insights',
        category: 'Analytics',
        status: 'In Progress',
        priority: 'Medium',
        generatedDate: '2024-01-10',
        lastUpdated: '4 hours ago',
        customerCount: 502,
        totalValue: 8500000,
        format: 'Excel',
        size: '1.8 MB',
        generatedBy: 'Al Razzaq',
        description: 'Analysis of customer engagement patterns, product usage, and satisfaction metrics for December 2023.',
        tags: ['Customer', 'Engagement', 'Analytics', 'December 2023'],
        scheduled: false
      },
      {
        id: 3,
        title: 'Risk Assessment Report - Premium Customers',
        type: 'Risk Management',
        category: 'Compliance',
        status: 'Completed',
        priority: 'Critical',
        generatedDate: '2024-01-08',
        lastUpdated: '1 day ago',
        customerCount: 23,
        totalValue: 1200000,
        format: 'PDF',
        size: '3.1 MB',
        generatedBy: 'Al Razzaq',
        description: 'Comprehensive risk assessment for premium customer segment, including portfolio stress testing.',
        tags: ['Risk', 'Premium', 'Compliance', 'Stress Testing'],
        scheduled: false
      },
      {
        id: 4,
        title: 'Investment Product Performance Comparison',
        type: 'Product Analysis',
        category: 'Performance',
        status: 'Completed',
        priority: 'Medium',
        generatedDate: '2024-01-05',
        lastUpdated: '2 days ago',
        customerCount: 187,
        totalValue: 4500000,
        format: 'PowerPoint',
        size: '4.2 MB',
        generatedBy: 'Al Razzaq',
        description: 'Comparative analysis of investment product performance across different market conditions.',
        tags: ['Investment', 'Product', 'Performance', 'Comparison'],
        scheduled: false
      },
      {
        id: 5,
        title: 'Customer Segmentation Analysis',
        type: 'Customer Insights',
        category: 'Analytics',
        status: 'Completed',
        priority: 'Low',
        generatedDate: '2024-01-03',
        lastUpdated: '3 days ago',
        customerCount: 502,
        totalValue: 8500000,
        format: 'Excel',
        size: '2.7 MB',
        generatedBy: 'Al Razzaq',
        description: 'Detailed customer segmentation analysis based on behavior, preferences, and financial profiles.',
        tags: ['Customer', 'Segmentation', 'Behavior', 'Analytics'],
        scheduled: false
      },
      {
        id: 6,
        title: 'Monthly Revenue Forecast - Q1 2024',
        type: 'Financial Planning',
        category: 'Forecasting',
        status: 'Scheduled',
        priority: 'High',
        generatedDate: '2024-01-20',
        lastUpdated: '4 days ago',
        customerCount: 502,
        totalValue: 8500000,
        format: 'PDF',
        size: '1.9 MB',
        generatedBy: 'Al Razzaq',
        description: 'Monthly revenue forecasting for Q1 2024 based on current customer base and market trends.',
        tags: ['Revenue', 'Forecasting', 'Q1 2024', 'Financial Planning'],
        scheduled: true
      },
      {
        id: 7,
        title: 'Customer Satisfaction Survey Results',
        type: 'Customer Feedback',
        category: 'Analytics',
        status: 'Completed',
        priority: 'Medium',
        generatedDate: '2023-12-28',
        lastUpdated: '1 week ago',
        customerCount: 156,
        totalValue: 2800000,
        format: 'PDF',
        size: '2.1 MB',
        generatedBy: 'Al Razzaq',
        description: 'Analysis of customer satisfaction survey results with actionable insights and recommendations.',
        tags: ['Customer', 'Satisfaction', 'Survey', 'Feedback'],
        scheduled: false
      },
      {
        id: 8,
        title: 'Regulatory Compliance Report',
        type: 'Compliance',
        category: 'Regulatory',
        status: 'In Progress',
        priority: 'Critical',
        generatedDate: '2024-01-12',
        lastUpdated: '2 hours ago',
        customerCount: 502,
        totalValue: 8500000,
        format: 'PDF',
        size: '5.3 MB',
        generatedBy: 'Al Razzaq',
        description: 'Comprehensive regulatory compliance report covering all customer segments and products.',
        tags: ['Compliance', 'Regulatory', 'Audit', 'Legal'],
        scheduled: false
      }
    ];

    setReports(mockReports);
  }, []);

  const handleReportSelect = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReports.length === reports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(reports.map(report => report.id));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'In Progress':
        return 'bg-[#FFA726] text-[#0A0A0A]';
      case 'Scheduled':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Pending':
        return 'bg-[#808080] text-[#FFFFFF]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-[#FF6B6B] text-[#FFFFFF]';
      case 'High':
        return 'bg-[#FFA726] text-[#0A0A0A]';
      case 'Medium':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Low':
        return 'bg-[#808080] text-[#FFFFFF]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Performance':
        return <TrendingUp className="text-[#B4E50D]" />;
      case 'Analytics':
        return <Assessment className="text-[#B4E50D]" />;
      case 'Compliance':
        return <CheckCircle className="text-[#B4E50D]" />;
      case 'Forecasting':
        return <TrendingUp className="text-[#B4E50D]" />;
      case 'Regulatory':
        return <Warning className="text-[#FFA726]" />;
      default:
        return <BarChart className="text-[#B0B0B0]" />;
    }
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'PDF':
        return <BarChart className="text-[#FF6B6B]" />;
      case 'Excel':
        return <BarChart className="text-[#B4E50D]" />;
      case 'PowerPoint':
        return <BarChart className="text-[#FFA726]" />;
      default:
        return <BarChart className="text-[#B0B0B0]" />;
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || report.category === filter;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReports = filteredReports.slice(startIndex, endIndex);

  const totalReports = reports.length;
  const completedReports = reports.filter(r => r.status === 'Completed').length;
  const totalValue = reports.reduce((sum, r) => sum + r.totalValue, 0);
  const scheduledReports = reports.filter(r => r.scheduled).length;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-[#FFFFFF]">Reports & Analytics</h1>
          <p className="text-[#B0B0B0] mt-1">Generate and manage comprehensive customer and portfolio reports</p>
        </div>
        <div className="flex items-center space-x-6">
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Total Reports</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{totalReports}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Completed</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{completedReports}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Total Value</div>
            <div className="text-2xl font-bold text-[#B4E50D]">${(totalValue / 1000000).toFixed(1)}M</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Scheduled</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{scheduledReports}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#2A2A2A]">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-[#B0B0B0]" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] placeholder-[#B0B0B0] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            >
              <option value="all">All Categories</option>
              <option value="Performance">Performance</option>
              <option value="Analytics">Analytics</option>
              <option value="Compliance">Compliance</option>
              <option value="Forecasting">Forecasting</option>
              <option value="Regulatory">Regulatory</option>
            </select>
            
            <button className="p-3 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-xl text-[#B0B0B0] hover:text-[#B4E50D] transition-all duration-200">
              <FilterList />
            </button>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] overflow-hidden">
        {/* Reports List Header */}
        <div className="bg-[#2A2A2A] px-6 py-4 border-b border-[#3A3A3A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedReports.length === reports.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2"
              />
              <span className="text-sm text-[#B0B0B0]">
                {selectedReports.length > 0 ? `${selectedReports.length} selected` : 'Select all'}
              </span>
            </div>
            
            {selectedReports.length > 0 && (
              <div className="flex items-center space-x-2">
                <button className="p-2 bg-[#B4E50D] hover:bg-[#B4E50D] rounded-lg text-[#0A0A0A] transition-colors">
                  <Download />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                  <Schedule />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#FF6B6B] transition-colors">
                  <Delete />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Report Items */}
        <div className="divide-y divide-[#2A2A2A]">
          {currentReports.map((report) => (
            <div 
              key={report.id}
              className="px-6 py-4 hover:bg-[#2A2A2A] transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  checked={selectedReports.includes(report.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleReportSelect(report.id);
                  }}
                  className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2 mt-1"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(report.category)}
                        <span className="text-sm text-[#808080]">{report.category}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(report.priority)}`}>
                        {report.priority}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-[#B0B0B0]">
                      <span>Updated: {report.lastUpdated}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-medium text-[#FFFFFF] text-lg">
                          {report.title}
                        </span>
                        <span className="text-sm text-[#B0B0B0]">({report.type})</span>
                      </div>
                      
                      <p className="text-sm text-[#B0B0B0] mb-3">
                        {report.description}
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 text-sm">
                            <People className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Customers:</span>
                            <span className="text-[#FFFFFF] font-medium">{report.customerCount}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <AttachMoney className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Value:</span>
                            <span className="text-[#B4E50D] font-medium">${(report.totalValue / 1000).toFixed(0)}K</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 text-sm">
                            <CalendarToday className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Generated:</span>
                            <span className="text-[#FFFFFF]">{report.generatedDate}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Business className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">By:</span>
                            <span className="text-[#FFFFFF]">{report.generatedBy}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 text-sm">
                            {getFormatIcon(report.format)}
                            <span className="text-[#B0B0B0]">Format:</span>
                            <span className="text-[#FFFFFF]">{report.format}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <BarChart className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Size:</span>
                            <span className="text-[#FFFFFF]">{report.size}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {report.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-[#2A2A2A] text-[#B0B0B0] rounded-lg text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-4">
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <Visibility />
                      </button>
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <Download />
                      </button>
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <MoreVert />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-[#2A2A2A] border-t border-[#3A3A3A]">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#B0B0B0]">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredReports.length)} of {filteredReports.length} reports
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsDashboard;
